import { ChangeEvent, FocusEvent, ReactElement, useState } from 'react';
import InputMask from '@mona-health/react-input-mask';

import { InputFailureSvg, InputSuccessSvg } from '@cashier/cashier-assets';
import {
  Directions,
  PaymentFormField,
  ValidationStatuses,
} from '@cashier/cashier-model';

import { cn } from '../../utils';

import defaultStyles from './defaultStyles.module.scss';
import inlineStyles from './inlineStyles.module.scss';

export interface MaskedInputProps {
  field: PaymentFormField;
  onChange: (value: string, isFocused: boolean) => void;
  direction: string;
  labelPosition: 'inline' | 'default';
  validationStatus: ValidationStatuses;
  isDisabled: boolean;
}

export const MaskedInput = ({
  field,
  onChange,
  direction,
  validationStatus,
  labelPosition,
  isDisabled,
}: MaskedInputProps): ReactElement => {
  const { type, placeholder, mask, name, label, value } = field;
  const isDefaultLabelPosition = labelPosition === 'default';
  const isStaticLabel = !!placeholder;
  const styleModule = isDefaultLabelPosition ? defaultStyles : inlineStyles;

  const [isFocused, setIsFocused] = useState(false);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value, true);
  };

  const handleFocus = (e: FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    onChange(e.target.value, true);
  };

  const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    onChange(e.target.value, false);
  };

  const input = (
    <InputMask
      data-test={`${name}-input`}
      type={type || 'text'}
      id={name}
      name={name}
      className={cn({
        [styleModule.input]: true,
        [styleModule.active]: isFocused,
        [styleModule.error]: validationStatus === ValidationStatuses.FAILED,
        [styleModule.rtl]: direction === Directions.Rtl,
      })}
      onChange={handleChange}
      placeholder={placeholder || ''}
      mask={mask}
      value={value || undefined}
      disabled={isDisabled}
      onFocus={handleFocus}
      onBlur={handleBlur}
    />
  );

  return (
    <div className={defaultStyles.wrapper}>
      {isDefaultLabelPosition && (
        <label
          htmlFor={name}
          className={defaultStyles.inputLabel}
          data-test={`${name}-input-label`}
        >
          {label}
        </label>
      )}

      {isDefaultLabelPosition ? (
        input
      ) : (
        <div
          className={cn({
            [inlineStyles.inputWrapper]: true,
            [inlineStyles.active]: isFocused,
            [inlineStyles.error]:
              validationStatus === ValidationStatuses.FAILED,
            [inlineStyles.rtl]: direction === Directions.Rtl,
          })}
        >
          {input}

          <label
            className={cn({
              [inlineStyles.label]: true,
              [inlineStyles.dynamicLabel]: !isStaticLabel,
              [inlineStyles.staticLabel]: isStaticLabel,
              [inlineStyles.rtl]: direction === Directions.Rtl,
            })}
          >
            {label}
          </label>

          <div
            className={cn({
              [inlineStyles.icon]: true,
              [inlineStyles.rtl]: direction === Directions.Rtl,
            })}
          >
            {validationStatus === ValidationStatuses.SUCCESS && (
              <div className={inlineStyles.success}>
                <InputSuccessSvg />
              </div>
            )}

            {validationStatus === ValidationStatuses.FAILED && (
              <div className={inlineStyles.error}>
                <InputFailureSvg />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
